import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { 
  ArrowLeft,
  Download,
  Leaf,
  Heart,
  AlertTriangle,
  CheckCircle2,
  Sparkles,
  Star,
  Video,
  Shield,
  Clock
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';


export const ConsultationReport = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { id: consultationId } = useParams();
  const [consultation, setConsultation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [upgrading, setUpgrading] = useState(false);
  const reportRef = useRef(null);

  useEffect(() => {
    fetchConsultation();
  }, [consultationId]);

  const fetchConsultation = async () => {
    try {
      const response = await api.get(`/api/consultations/${consultationId}`);
      setConsultation(response.data);
    } catch (error) {
      console.error('Error fetching consultation:', error);
      toast.error('Failed to load consultation');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = async (newPlan) => {
    setUpgrading(true);
    try {
      const response = await api.post(`/api/consultations/${consultationId}/upgrade`, {
        new_plan: newPlan,
        origin_url: window.location.origin
      });
      
      // Redirect to Stripe checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      console.error('Error initiating upgrade:', error);
      toast.error(error.response?.data?.detail || 'Failed to initiate upgrade');
      setUpgrading(false);
    }
  };

  const handleDownload = async () => {
    try {
      const html2canvas = (await import('html2canvas')).default;
      const jsPDF = (await import('jspdf')).default;
      
      const element = reportRef.current;
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        logging: false
      });
      
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      
      // Page dimensions with margins
      const pageWidth = 210;
      const pageHeight = 297;
      const marginTop = 15;
      const marginBottom = 15;
      const marginLeft = 10;
      const marginRight = 10;
      
      // Usable area
      const usableWidth = pageWidth - marginLeft - marginRight;
      const usableHeight = pageHeight - marginTop - marginBottom;
      
      // Calculate image dimensions to fit within margins
      const imgWidth = usableWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = marginTop;
      
      // First page
      pdf.addImage(imgData, 'PNG', marginLeft, position, imgWidth, imgHeight);
      heightLeft -= usableHeight;
      
      // Additional pages if needed
      while (heightLeft > 0) {
        position = marginTop - (imgHeight - heightLeft);
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', marginLeft, position, imgWidth, imgHeight);
        heightLeft -= usableHeight;
      }
      
      pdf.save(`ayurveda-consultation-${consultationId.slice(-8)}.pdf`);
      toast.success('Report downloaded!', { id: 'report-downloaded' });
    } catch (error) {
      console.error('Error downloading report:', error);
      toast.error('Failed to download report');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[hsl(40,23%,96%)]">
        <div className="w-12 h-12 border-4 border-[hsl(22,70%,44%)] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-[hsl(40,23%,96%)]" data-testid="consultation-report-page">
      {/* Header - No Print */}
      <header className="glass sticky top-0 z-50 border-b border-stone-200/50 no-print">
        <div className="max-w-4xl mx-auto px-4 md:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              onClick={() => navigate(`/consultation/${consultationId}/diagnosis`)}
              className="text-stone-600"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-semibold text-[hsl(120,10%,18%)]" style={{ fontFamily: 'Playfair Display, serif' }}>
                Consultation Report
              </h1>
              <p className="text-sm text-stone-500">Download your personalized report</p>
            </div>
          </div>
          <div className="flex gap-3">
            <Button
              onClick={handleDownload}
              className="btn-primary"
              data-testid="download-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Download PDF
            </Button>
          </div>
        </div>
      </header>

      {/* Report Content */}
      <main className="max-w-4xl mx-auto px-4 md:px-8 py-8">
        <div ref={reportRef} className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
          {/* Report Header */}
          <div className="report-header">
            <div className="flex items-center gap-4">
              <img 
                src="https://aiays.org/wp-content/uploads/2024/01/Untitled-13.png" 
                alt="Logo" 
                className="h-12 w-auto"
              />
              <div>
                <h2 className="text-2xl font-bold text-[hsl(120,10%,18%)]" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Ayurveda Consult
                </h2>
                <p className="text-stone-500 text-sm">Customised Report</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm text-stone-500">Report ID</p>
              <p className="font-mono text-[hsl(120,10%,18%)]">#{consultationId.slice(-8).toUpperCase()}</p>
              <p className="text-sm text-stone-500 mt-2">{formatDate(consultation?.created_at)}</p>
            </div>
          </div>

          {/* Client Info */}
          <div className="report-section">
            <div className="bg-stone-50 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-[hsl(25,30%,35%)] mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
                Patient Information
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-stone-500">Name</p>
                  <p className="font-medium text-[hsl(120,10%,18%)]">{user?.name}</p>
                </div>
                <div>
                  <p className="text-sm text-stone-500">Email</p>
                  <p className="font-medium text-[hsl(120,10%,18%)]">{user?.email}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Areas of Concern */}
          <div className="report-section">
            <h3 className="text-lg font-semibold text-[hsl(25,30%,35%)]" style={{ fontFamily: 'Playfair Display, serif' }}>
              Areas of Concern
            </h3>
            <div className="space-y-3">
              {consultation?.symptoms?.map((symptom, index) => (
                <div key={index} className="flex items-center justify-between bg-stone-50 rounded-lg p-3">
                  <div>
                    <span className="font-medium text-[hsl(120,10%,18%)]">{symptom.name}</span>
                    <span className="text-stone-500 text-sm ml-2">- Duration: {symptom.duration}</span>
                    {symptom.description && (
                      <p className="text-sm text-stone-600 mt-1">{symptom.description}</p>
                    )}
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    symptom.severity === 'mild' ? 'bg-green-100 text-green-700' :
                    symptom.severity === 'moderate' ? 'bg-amber-100 text-amber-700' :
                    'bg-red-100 text-red-700'
                  }`}>
                    {symptom.severity === 'severe' ? 'significant' : symptom.severity}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Patient Summary - Analysis of all submitted information */}
          {consultation?.patient_summary && (
            <div className="report-section">
              <div className="bg-gradient-to-r from-[hsl(22,70%,44%)/0.1] to-[hsl(43,70%,52%)/0.1] rounded-xl p-6 border border-[hsl(22,70%,44%)/0.2]">
                <h3 className="text-lg font-semibold text-[hsl(25,30%,35%)] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Comprehensive Health Assessment
                </h3>
                
                {consultation.patient_summary.key_symptoms?.length > 0 && (
                  <div className="mb-4">
                    <h4 className="text-sm font-medium text-stone-600 mb-2">Key Concerns Identified</h4>
                    <div className="flex flex-wrap gap-2">
                      {consultation.patient_summary.key_symptoms.map((item, i) => (
                        <span key={i} className="px-3 py-1 bg-red-50 text-red-700 rounded-full text-sm">
                          {item}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                
                {consultation.patient_summary.lifestyle_factors?.length > 0 && (
                  <div className="mb-4">
                    <h4 className="text-sm font-medium text-stone-600 mb-2">Lifestyle Factors Affecting Health</h4>
                    <div className="flex flex-wrap gap-2">
                      {consultation.patient_summary.lifestyle_factors.map((item, i) => (
                        <span key={i} className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm">
                          {item}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                
                {consultation.patient_summary.areas_of_concern?.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-stone-600 mb-2">Areas Requiring Attention</h4>
                    <div className="flex flex-wrap gap-2">
                      {consultation.patient_summary.areas_of_concern.map((item, i) => (
                        <span key={i} className="px-3 py-1 bg-amber-50 text-amber-700 rounded-full text-sm">
                          {item}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Lifestyle & Clarifying Answers Summary */}
          {(consultation?.lifestyle_answers || consultation?.clarifying_answers) && (
            <div className="report-section">
              <h3 className="text-lg font-semibold text-[hsl(25,30%,35%)] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                Patient Information Used for Analysis
              </h3>
              <div className="grid grid-cols-2 gap-4">
                {consultation.lifestyle_answers && Object.keys(consultation.lifestyle_answers).length > 0 && (
                  <div className="bg-stone-50 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-stone-600 mb-2">Lifestyle Factors</h4>
                    <ul className="space-y-1 text-sm text-stone-700">
                      {Object.entries(consultation.lifestyle_answers).map(([key, value]) => (
                        <li key={key}>
                          <span className="text-stone-500">{key.replace(/_/g, ' ')}:</span> {value}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {consultation.clarifying_answers && Object.keys(consultation.clarifying_answers).length > 0 && (
                  <div className="bg-stone-50 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-stone-600 mb-2">Additional Responses</h4>
                    <ul className="space-y-1 text-sm text-stone-700">
                      {Object.entries(consultation.clarifying_answers).map(([key, value]) => (
                        <li key={key}>
                          <span className="text-stone-500">Q{key.replace('q', '')}:</span> {value}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Ayurvedic Assessment */}
          {consultation?.diagnosis && (
            <div className="report-section">
              <div className="flex items-center gap-3 mb-4">
                <Leaf className="w-6 h-6 text-[hsl(22,70%,44%)]" />
                <h3 className="text-lg font-semibold text-[hsl(25,30%,35%)]" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Ayurvedic Assessment
                </h3>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {Object.entries(consultation.diagnosis).map(([key, value]) => (
                  <div key={key} className="bg-[hsl(22,70%,44%)/0.05] rounded-lg p-4">
                    <p className="text-sm text-stone-500 capitalize mb-1">{key.replace(/_/g, ' ')}</p>
                    <p className="text-[hsl(120,10%,18%)]">{value}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Modern Wellness Interpretation */}
          {consultation?.modern_interpretation && (
            <div className="report-section">
              <div className="flex items-center gap-3 mb-4">
                <Heart className="w-6 h-6 text-blue-600" />
                <h3 className="text-lg font-semibold text-[hsl(25,30%,35%)]" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Modern Wellness Interpretation
                </h3>
              </div>
              <p className="text-stone-700 leading-relaxed mb-4">
                {consultation.modern_interpretation.condition_overview}
              </p>
              {consultation.modern_interpretation.related_conditions?.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {consultation.modern_interpretation.related_conditions.map((condition, index) => (
                    <span key={index} className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm">
                      {condition}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Recommendations */}
          {consultation?.recommendations && (
            <>
              {/* Do's and Don'ts */}
              <div className="report-section grid grid-cols-2 gap-6">
                {consultation.recommendations.do_list?.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                      <h4 className="font-semibold text-emerald-700">Things to Follow</h4>
                    </div>
                    <ul className="space-y-2">
                      {consultation.recommendations.do_list.map((item, index) => (
                        <li key={index} className="text-sm text-stone-700 flex items-start gap-2">
                          <span className="text-emerald-500 mt-1">✓</span>
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {consultation.recommendations.avoid_list?.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <AlertTriangle className="w-5 h-5 text-red-600" />
                      <h4 className="font-semibold text-red-700">Things to Avoid</h4>
                    </div>
                    <ul className="space-y-2">
                      {consultation.recommendations.avoid_list.map((item, index) => (
                        <li key={index} className="text-sm text-stone-700 flex items-start gap-2">
                          <span className="text-red-500 mt-1">✗</span>
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              {/* Dietary Guidelines */}
              {consultation.recommendations.dietary_guidelines?.length > 0 && (
                <div className="report-section">
                  <h4 className="font-semibold text-[hsl(25,30%,35%)] mb-3">Dietary Guidelines</h4>
                  <ul className="grid grid-cols-2 gap-2">
                    {consultation.recommendations.dietary_guidelines.map((item, index) => (
                      <li key={index} className="text-sm text-stone-700 flex items-start gap-2">
                        <Leaf className="w-4 h-4 text-[hsl(135,15%,33%)] mt-0.5 flex-shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Herbal Remedies */}
              {consultation.recommendations.herbal_remedies?.length > 0 && (
                <div className="report-section">
                  <h4 className="font-semibold text-[hsl(25,30%,35%)] mb-3">Herbal Remedies</h4>
                  <div className="space-y-4">
                    {consultation.recommendations.herbal_remedies.map((remedy, index) => (
                      <div key={index} className="border border-stone-200 rounded-lg p-4">
                        <div className="flex justify-between items-start mb-2">
                          <span className="font-medium text-[hsl(120,10%,18%)]">{remedy.name}</span>
                          <span className="text-sm font-mono text-[hsl(22,70%,44%)]">{remedy.dosage}</span>
                        </div>
                        <p className="text-sm text-stone-600 mb-2">{remedy.benefits}</p>
                        {remedy.warning && (
                          <div className="bg-amber-50 border border-amber-200 rounded p-2 flex items-start gap-2">
                            <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                            <p className="text-xs text-amber-800">{remedy.warning}</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Panchkarma */}
              {consultation.recommendations.panchkarma_recommendation?.recommended && (
                <div className="report-section">
                  <div className="bg-[hsl(43,70%,52%)/0.1] border border-[hsl(43,70%,52%)/0.3] rounded-xl p-6">
                    <div className="flex items-center gap-3 mb-3">
                      <Sparkles className="w-6 h-6 text-[hsl(43,70%,52%)]" />
                      <h4 className="font-semibold text-[hsl(25,30%,35%)]">Panchkarma Recommended</h4>
                    </div>
                    <p className="text-stone-600 mb-3">
                      {consultation.recommendations.panchkarma_recommendation.reason}
                    </p>
                    {consultation.recommendations.panchkarma_recommendation.treatments?.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {consultation.recommendations.panchkarma_recommendation.treatments.map((treatment, index) => (
                          <span key={index} className="px-3 py-1 bg-[hsl(43,70%,52%)] text-white rounded-full text-sm">
                            {treatment}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}

          {/* AYUSH CARE 15 Section - Only for consultation plan */}
          {consultation?.plan === 'consultation' && (
            <div className="mt-8 bg-gradient-to-r from-emerald-50 to-teal-50 border-2 border-emerald-200 rounded-2xl p-6">
              <div className="text-center mb-4">
                <span className="text-2xl">🌿</span>
                <h3 className="text-xl font-bold text-emerald-800 mt-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                  AYUSH CARE 15
                </h3>
                <p className="text-emerald-700 font-medium">✨ 15-Day Complimentary Supportive Healing Plan ✨</p>
              </div>
              <div className="grid sm:grid-cols-2 gap-3 mb-4">
                <div className="flex items-center gap-2 text-sm text-emerald-700">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                  Personalised Diet Consultation
                </div>
                <div className="flex items-center gap-2 text-sm text-emerald-700">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                  Holistic Health Evaluation
                </div>
                <div className="flex items-center gap-2 text-sm text-emerald-700">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                  Accountability Buddy
                </div>
                <div className="flex items-center gap-2 text-sm text-emerald-700">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                  Daily Follow-up on Call/Message
                </div>
                <div className="flex items-center gap-2 text-sm text-emerald-700 sm:col-span-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                  Lifestyle & Diet Guidance for Lasting Health
                </div>
              </div>
              <p className="text-sm text-emerald-600 text-center italic">
                🤍 Offered as a privilege to our patients—so we can support, guide, and walk with you on your journey toward healing, because We Care.
              </p>
              <p className="text-xs text-emerald-500 text-center mt-2">
                ➡️ Continued care plans available thereafter.
              </p>
            </div>
          )}

          {/* Disclaimer */}
          <div className="mt-8 pt-6 border-t border-stone-200">
            <p className="text-xs text-stone-500 leading-relaxed">
              <strong>Disclaimer:</strong> This assessment is generated by AI using Ayurvedic principles and user-provided information. 
              It is for educational purposes only and should not be considered as medical advice. 
              Our doctors are here for reviewing, validating, discussing, and guiding — not diagnosing diseases, 
              prescribing medicines, or providing treatment. Always consult with a qualified healthcare provider for any medical concerns. 
              The herbal remedies suggested should be taken under the guidance of a certified Ayurvedic practitioner.
            </p>
          </div>

          {/* Upgrade Options - Show only for basic plan */}
          {consultation?.plan === 'basic' && (
            <div className="mt-8 pt-6 border-t-2 border-[hsl(22,70%,44%)/0.3] no-print">
              <div className="text-center mb-6">
                <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-700 px-4 py-1.5 rounded-full text-sm font-semibold mb-3">
                  <Clock className="w-4 h-4" />
                  Limited Time Offer
                </div>
                <h3 className="text-xl font-semibold text-[hsl(120,10%,18%)]" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Enhance Your Consultation Experience
                </h3>
                <p className="text-stone-600 mt-2">Upgrade your plan and save 20%!</p>
              </div>
              
              <div className="grid md:grid-cols-2 gap-4">
                {/* Doctor-Reviewed Upgrade */}
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200 relative overflow-hidden">
                  <div className="absolute top-2 right-2 bg-red-500 text-white text-xs px-2 py-1 rounded-full font-medium">
                    20% OFF
                  </div>
                  <div className="absolute top-2 left-2 bg-emerald-500 text-white text-xs px-2 py-1 rounded-full font-medium">
                    Safe
                  </div>
                  <div className="flex items-center gap-3 mb-3 mt-4">
                    <Shield className="w-8 h-8 text-blue-600" />
                    <div>
                      <h4 className="font-semibold text-[hsl(120,10%,18%)]">Doctor-Reviewed</h4>
                      <p className="text-sm text-stone-600">Expert review of your report</p>
                    </div>
                  </div>
                  <ul className="text-sm text-stone-600 space-y-1 mb-4">
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-600" />
                      Validated by certified Ayurvedic doctor
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-600" />
                      Additional personalized notes
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-600" />
                      Priority email support
                    </li>
                  </ul>
                  <p className="text-xs text-amber-600 italic mb-3">Non-clinical review. Does not replace medical advice.</p>
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-stone-400 line-through text-sm">$10</span>
                      <span className="text-2xl font-bold text-blue-600 ml-2">$8</span>
                      <span className="text-sm text-stone-500"> USD</span>
                    </div>
                    <Button 
                      onClick={() => handleUpgrade('verified')}
                      disabled={upgrading}
                      className="bg-blue-600 hover:bg-blue-700 text-white"
                      data-testid="upgrade-verified-btn"
                    >
                      {upgrading ? 'Processing...' : 'Upgrade Now'}
                    </Button>
                  </div>
                </div>

                {/* 1-on-1 Consultation Upgrade */}
                <div className="bg-gradient-to-br from-[hsl(22,70%,44%)/0.1] to-[hsl(43,70%,52%)/0.1] rounded-xl p-6 border-2 border-[hsl(22,70%,44%)/0.5] relative overflow-hidden">
                  <div className="absolute top-2 right-2 bg-red-500 text-white text-xs px-2 py-1 rounded-full font-medium">
                    20% OFF
                  </div>
                  <div className="absolute top-2 left-2 bg-[hsl(22,70%,44%)] text-white text-xs px-2 py-1 rounded-full font-medium">
                    Value
                  </div>
                  <div className="flex items-center gap-3 mb-3 mt-4">
                    <Video className="w-8 h-8 text-[hsl(22,70%,44%)]" />
                    <div>
                      <h4 className="font-semibold text-[hsl(120,10%,18%)]">1-on-1 Consultation</h4>
                      <p className="text-sm text-stone-600">Personal video call with doctor</p>
                    </div>
                  </div>
                  <ul className="text-sm text-stone-600 space-y-1 mb-4">
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-600" />
                      Everything in Doctor-Reviewed
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-600" />
                      45-min personal video consultation
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-600" />
                      Ask questions in real-time
                    </li>
                  </ul>
                  <p className="text-xs text-amber-600 italic mb-3">Ayurvedic diet and lifestyle guidance only.</p>
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-stone-400 line-through text-sm">$40</span>
                      <span className="text-2xl font-bold text-[hsl(22,70%,44%)] ml-2">$32</span>
                      <span className="text-sm text-stone-500"> USD</span>
                    </div>
                    <Button 
                      onClick={() => handleUpgrade('consultation')}
                      disabled={upgrading}
                      className="btn-primary"
                      data-testid="upgrade-consultation-btn"
                    >
                      {upgrading ? 'Processing...' : 'Upgrade Now'}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Show upgrade to consultation only for verified plan */}
          {consultation?.plan === 'verified' && (
            <div className="mt-8 pt-6 border-t-2 border-[hsl(22,70%,44%)/0.3] no-print">
              <div className="bg-gradient-to-br from-[hsl(22,70%,44%)/0.1] to-[hsl(43,70%,52%)/0.1] rounded-xl p-6 border-2 border-[hsl(22,70%,44%)/0.5] relative overflow-hidden">
                <div className="absolute top-2 right-2 bg-red-500 text-white text-xs px-2 py-1 rounded-full font-medium">
                  20% OFF
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <Video className="w-10 h-10 text-[hsl(22,70%,44%)]" />
                    <div>
                      <h4 className="font-semibold text-lg text-[hsl(120,10%,18%)]">Want to discuss your report personally?</h4>
                      <p className="text-stone-600">Upgrade to a 45-min video consultation with our Ayurvedic physician</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="mb-2">
                      <span className="text-stone-400 line-through text-sm">$30</span>
                      <span className="text-2xl font-bold text-[hsl(22,70%,44%)] ml-2">$24</span>
                      <span className="text-sm text-stone-500"> USD</span>
                    </div>
                    <Button 
                      onClick={() => handleUpgrade('consultation')}
                      disabled={upgrading}
                      className="btn-primary"
                      data-testid="upgrade-consultation-btn"
                    >
                      {upgrading ? 'Processing...' : 'Upgrade to 1-on-1'}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="mt-6 pt-6 border-t border-stone-200 flex items-center justify-between text-sm text-stone-500">
            <span>© 2025 Ayurveda Consult</span>
            <span>Generated on {new Date().toLocaleDateString()}</span>
          </div>
        </div>
      </main>
    </div>
  );
};
